// Single writer (SPEC §4): only the window holding this Web Lock writes to the folder.
// Other windows show "Hashquill is open in another window" with "Use this window" (steal).

export const LOCK_NAME = 'hashquill-folder';

export type WriteLock = {
  /** Resolves false if another window holds the lock (and we didn't steal it). */
  readonly acquired: Promise<boolean>;
  /** Called if another window takes the lock away from us. */
  onLost(cb: () => void): void;
  release(): void;
};

export function requestWriteLock({ steal = false, locks = navigator.locks }: { steal?: boolean; locks?: LockManager } = {}): WriteLock {
  let release!: () => void;
  const held = new Promise<void>((r) => { release = r; });
  let resolveAcquired!: (v: boolean) => void;
  const acquired = new Promise<boolean>((r) => { resolveAcquired = r; });
  const lostCallbacks: (() => void)[] = [];
  let gotIt = false;

  const opts: LockOptions = steal ? { steal: true } : { ifAvailable: true };
  locks.request(LOCK_NAME, opts, async (lock) => {
    if (!lock) { resolveAcquired(false); return; }
    gotIt = true;
    resolveAcquired(true);
    await held;
  }).catch((e: unknown) => {
    // A steal by another window rejects our request with AbortError.
    if (gotIt && e instanceof DOMException && e.name === 'AbortError') lostCallbacks.forEach((cb) => cb());
    else resolveAcquired(false);
  });

  return {
    acquired,
    onLost: (cb) => { lostCallbacks.push(cb); },
    release: () => release(),
  };
}
